import { get, post } from './base'
import { convertToCamelCase } from '@/models/utils/casing'
import { ResourceLocation, UnitNumber } from '@/models/unit_number'
import { computed, Ref, unref } from 'vue'
import { useMutation, useQuery } from '@tanstack/vue-query'

const unitsEndPoint = '/api/v2/units'
const unitsV3EndPoint = '/api/v3/units'

let unitListController: AbortController | undefined

export function getUnitNumbers() {
  unitListController?.abort()
  unitListController = new AbortController()

  return get(`${unitsV3EndPoint}/all`, {
    signal: unitListController.signal,
  })
}

export function updateUnitStatus(id, status) {
  return post(`${unitsEndPoint}/${id}/status`, {
    data: {
      id,
      status,
    },
  })
}

export function getById(id: number | string, signal?: AbortSignal) {
  return get<UnitNumber>(`${unitsV3EndPoint}/${id}`, { signal }).then((res) =>
    convertToCamelCase(res.data),
  )
}

type MaybeRef<T> = Ref<T> | T

export function useResourceQuery(resourceId?: MaybeRef<number | string | null | undefined>) {
  const computedId = computed(() => unref(resourceId))
  const queryKey = computed(() => ['resource', computedId.value])
  const enabled = computed(() => {
    return computedId.value !== null && computedId.value !== undefined
  })

  return useQuery({
    queryKey,
    queryFn: ({ signal }) => getById(computedId.value!, signal),
    enabled,
  })
}

export function useUpdateResourceStatusMutation() {
  return useMutation({
    mutationFn: (variables: Record<string, any>) =>
      updateUnitStatus(variables.id, variables.status),
  })
}

function getResourceLocationById(resourceId: number | string, signal?: AbortSignal) {
  return get<ResourceLocation>(`${unitsV3EndPoint}/locations/${resourceId}`, { signal }).then(
    (res) => convertToCamelCase(res.data),
  )
}

export function useResourceLocationQuery(
  resourceId?: MaybeRef<number | string | null | undefined>,
) {
  const computedId = computed(() => unref(resourceId))
  const queryKey = computed(() => ['resource', 'location', computedId.value])
  const enabled = computed(() => {
    return computedId.value !== null && computedId.value !== undefined
  })

  return useQuery({
    queryKey,
    queryFn: ({ signal }) => getResourceLocationById(computedId.value!, signal),
    enabled,
  })
}
